import React from 'react';
import { Box, Skeleton, Stack, Tooltip } from '@mui/material';

import { consoleRhythm, consoleTokens } from './consoleTokens';

export interface ConsoleStat {
  key: string;
  /** Lowercase noun: `collected`, `sessions`, `unsigned`. */
  label: string;
  /** Preformatted display value — formatting is the host's job. */
  value: string;
  /** Percent change vs prior period; null/undefined renders no delta. */
  deltaPct?: number | null;
  /** Hover explanation of what the delta compares against. */
  deltaTooltip?: string;
  /** Render the value amber (e.g. unsigned > 0). */
  warning?: boolean;
}

const Delta: React.FC<{ pct: number; tooltip?: string }> = ({ pct, tooltip }) => {
  const body = (
    <Box
      component="span"
      sx={{
        color: pct >= 0 ? consoleTokens.green : consoleTokens.red,
        cursor: tooltip ? 'help' : undefined,
        fontFamily: consoleTokens.mono,
        fontSize: '0.6875rem',
        fontWeight: 500,
        ml: 0.75,
      }}
    >
      {pct >= 0 ? '▲' : '▼'}
      {Math.abs(pct).toFixed(1)}%
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
};

export interface ConsoleStatStripProps {
  stats: ConsoleStat[];
  /** Renders skeleton placeholders sized like real stats. */
  loading?: boolean;
  /** Skeleton count while loading with no stats metadata yet. */
  skeletonCount?: number;
}

/** Dense headline numbers above the table — answers first, evidence below. */
export const ConsoleStatStrip: React.FC<ConsoleStatStripProps> = ({
  stats,
  loading = false,
  skeletonCount = 3,
}) => (
  <Stack
    direction="row"
    spacing={4}
    sx={{
      alignItems: 'center',
      borderBottom: `1px solid ${consoleTokens.line}`,
      height: consoleRhythm.statStrip,
      px: 2,
    }}
  >
    {loading
      ? Array.from(
          { length: stats.length || skeletonCount },
          (_, index) => index,
        ).map(index => (
          <Stack direction="row" key={index} spacing={1} sx={{ alignItems: 'baseline' }}>
            <Skeleton variant="text" width={72} sx={{ fontSize: '1.25rem' }} />
            {stats[index] ? (
              <Box component="span" sx={{ color: consoleTokens.dim, fontSize: '0.75rem' }}>
                {stats[index].label}
              </Box>
            ) : (
              <Skeleton variant="text" width={48} sx={{ fontSize: '0.75rem' }} />
            )}
          </Stack>
        ))
      : stats.map(stat => (
          <Box key={stat.key}>
            <Box
              component="span"
              sx={{
                color: stat.warning ? consoleTokens.amber : consoleTokens.ink,
                fontFamily: consoleTokens.mono,
                fontSize: '1.25rem',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600,
              }}
            >
              {stat.value}
            </Box>
            {stat.deltaPct != null && (
              <Delta pct={stat.deltaPct} tooltip={stat.deltaTooltip} />
            )}
            <Box component="span" sx={{ color: consoleTokens.dim, fontSize: '0.75rem', ml: 1 }}>
              {stat.label}
            </Box>
          </Box>
        ))}
  </Stack>
);
